import type { SolanaWalletState, TokenBalance } from "./solanaTypes";
import { getSupabase } from "../auth/supabaseClient";
import { fetchTokenUsdPrice } from "./pricingService";

const TEST_MORALIS_API_KEY =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: Record<string, string> }).env?.MODE === "test" &&
    typeof globalThis !== "undefined"
      ? (globalThis as { __VITEST_MORALIS_API_KEY__?: string }).__VITEST_MORALIS_API_KEY__
      : undefined);

const MORALIS_API_KEY =
  TEST_MORALIS_API_KEY ??
  ((typeof import.meta !== "undefined" && (import.meta as { env?: Record<string, string> }).env?.VITE_MORALIS_API_KEY) || "");

const MORALIS_SOLANA_BASE = "https://solana-gateway.moralis.io";

// Re-export fetchSolUsdPrice as a backward-compatible alias for fetchTokenUsdPrice (SOL mint)
// Existing callers (tokenShopService, etc.) should migrate to fetchTokenUsdPrice from pricingService.
const SOL_NATIVE_MINT = "So11111111111111111111111111111111111111112";
export { fetchTokenUsdPrice as fetchSolUsdPrice } from "./pricingService";


const EMPTY_STATE: SolanaWalletState = {
  address: null,
  isConnected: false,
  tokenBalances: [],
  balanceLoading: false,
  balanceError: null,
};

export class SolanaService {
  private state: SolanaWalletState = { ...EMPTY_STATE };

  /** Returns the current Solana wallet state. */
  getState(): SolanaWalletState {
    return this.state;
  }

  /** AppKit handles wallet discovery (including mobile deep links). Always available. */
  isWalletAvailable(): boolean {
    return true;
  }

  /**
   * Returns shortened address like "AbCd...xYzW" (first 4 + last 4 chars) or null.
   */
  getShortenedAddress(): string | null {
    const addr = this.state.address;
    if (!addr || addr.length < 8) return null;
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`;
  }

  /**
   * Returns the current Solana provider from AppKit, or null.
   */
  async getProvider(): Promise<unknown> {
    const { getSolProvider } = await import("./appKitService");
    return getSolProvider();
  }

  /**
   * Connect using the AppKit Solana provider.
   * Saves address to Supabase profiles.primary_sol_wallet.
   * Triggers fetchBalances() fire-and-forget.
   */
  async connect(): Promise<{ success: boolean; error?: string }> {
    const { getSolProvider } = await import("./appKitService");
    const rawProvider = getSolProvider();
    if (rawProvider == null) {
      return { success: false, error: "No Solana provider — connect wallet via AppKit first" };
    }
    return this._connectWithProvider(rawProvider);
  }

  /**
   * Connect using a provider received from subscribeProviders callback.
   * Avoids redundant getSolProvider() call when AppKit auto-reconnects.
   */
  async connectFromProvider(rawProvider: unknown): Promise<{ success: boolean; error?: string }> {
    if (rawProvider == null) {
      return { success: false, error: "No provider supplied" };
    }
    return this._connectWithProvider(rawProvider);
  }

  /** Reset state on wallet disconnect (called by main.ts when subscribeProviders fires). */
  onDisconnect(): void {
    this.state = { ...EMPTY_STATE };
  }

  /**
   * Sign and send a Solana transaction via the AppKit provider.
   * Replaces the direct signAndSendTransaction cast in tokenShopService.
   */
  async signAndSend(tx: import("@solana/web3.js").Transaction): Promise<string> {
    const { initAppKit, getSolProvider } = await import("./appKitService");
    initAppKit(); // ensure AppKit is initialized (idempotent)
    const rawProvider = getSolProvider();
    if (rawProvider == null) {
      throw new Error("No Solana wallet connected");
    }

    const provider = rawProvider as {
      sendTransaction?: (tx: any, connection: any) => Promise<{ signature: string } | string>;
      signAndSendTransaction?: (tx: any) => Promise<{ signature: string }>;
    };

    // Refresh blockhash — the server-built tx may have a stale one by signing time
    const { Connection } = await import("@solana/web3.js");
    const rpcUrl = (import.meta.env.VITE_SOLANA_RPC_URL as string) || "https://api.mainnet-beta.solana.com";
    const connection = new Connection(rpcUrl);
    try {
      const { blockhash } = await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
    } catch (bhErr) {
      console.warn("[sol] blockhash refresh failed, using original:", bhErr instanceof Error ? bhErr.message : String(bhErr));
    }

    // Try AppKit's sendTransaction first, fall back to Phantom's signAndSendTransaction
    if (typeof provider.sendTransaction === "function") {
      const result = await provider.sendTransaction(tx, connection);
      return typeof result === "string" ? result : result.signature;
    }

    if (typeof provider.signAndSendTransaction === "function") {
      const result = await provider.signAndSendTransaction(tx);
      return result.signature;
    }

    throw new Error("Wallet provider missing sendTransaction");
  }

  private async _connectWithProvider(rawProvider: unknown): Promise<{ success: boolean; error?: string }> {
    try {
      // AppKit providers expose the address after connection via getAddress or publicKey
      const p = rawProvider as any;
      let address: string | null = null;

      // Try publicKey (Phantom/Solflare style)
      if (p.publicKey?.toBase58) {
        address = p.publicKey.toBase58();
      } else if (typeof p.getAddress === "function") {
        address = await p.getAddress();
      }

      if (!address) {
        // Try connecting if the provider supports it
        if (typeof p.connect === "function") {
          const resp = await p.connect();
          address = resp?.publicKey?.toBase58?.() ?? null;
        }
      }

      if (!address) {
        return { success: false, error: "Could not get Solana address from provider" };
      }

      // Save address to Supabase
      try {
        const supabase = getSupabase();
        const { data: { user } } = await supabase.auth.getUser();
        if (user != null) {
          await supabase
            .from("profiles")
            .update({ primary_sol_wallet: address })
            .eq("id", user.id);
        }
      } catch (dbErr) {
        console.warn("[sol] Failed to persist sol wallet address:", dbErr instanceof Error ? dbErr.message : String(dbErr));
      }

      this.state = {
        ...EMPTY_STATE,
        address,
        isConnected: true,
      };

      void this.fetchBalances();
      return { success: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[sol] connect error:", msg);
      return { success: false, error: msg };
    }
  }

  /**
   * Disconnect from the Solana wallet.
   * Clears primary_sol_wallet from Supabase profile.
   * Resets local state.
   */
  async disconnect(): Promise<boolean> {
    try {
      const supabase = getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (user != null) {
        await supabase
          .from("profiles")
          .update({ primary_sol_wallet: null })
          .eq("id", user.id);
      }
    } catch (dbErr) {
      console.warn("[sol] Failed to clear sol wallet address:", dbErr instanceof Error ? dbErr.message : String(dbErr));
    }

    this.state = { ...EMPTY_STATE };
    return true;
  }

  /**
   * Load cached Solana wallet state from Supabase profiles.primary_sol_wallet.
   * Called on login. Triggers fetchBalances() fire-and-forget if connected.
   */
  async loadCachedState(userId: string): Promise<void> {
    try {
      const supabase = getSupabase();
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("primary_sol_wallet")
        .eq("id", userId)
        .single();

      if (profileError || profile == null) {
        console.warn("[sol] loadCachedState profile fetch failed:", profileError?.message ?? "no data");
        this.state = { ...EMPTY_STATE };
        return;
      }

      const address = (profile.primary_sol_wallet as string | null) ?? null;
      if (address) {
        this.state = {
          ...EMPTY_STATE,
          address,
          isConnected: true,
        };
        // Fire-and-forget balance fetch
        void this.fetchBalances();
      } else {
        this.state = { ...EMPTY_STATE };
      }
    } catch (err) {
      console.warn("[sol] loadCachedState error:", err instanceof Error ? err.message : String(err));
      this.state = { ...EMPTY_STATE };
    }
  }

  /**
   * Fetch native SOL balance + SPL token balances via Moralis Solana API.
   * No @solana/web3.js dependency — uses REST endpoints.
   */
  async fetchBalances(): Promise<void> {
    const { address } = this.state;
    if (!address) return;
    if (!MORALIS_API_KEY) {
      console.warn("[sol] VITE_MORALIS_API_KEY not set, skipping balance fetch");
      return;
    }

    this.state = { ...this.state, balanceLoading: true };
    if (import.meta.env.DEV) console.log("[sol] fetchBalances starting for", address);

    const headers = { accept: "application/json", "X-API-Key": MORALIS_API_KEY };

    try {
      const updatedBalances: TokenBalance[] = [];

      // Fetch SOL/USD price (cached, 1 API call per 5 min max) — now routed through pricingService
      const solUsdPrice = await fetchTokenUsdPrice(SOL_NATIVE_MINT);

      // Fetch native SOL balance via Moralis
      try {
        const solResp = await fetch(
          `${MORALIS_SOLANA_BASE}/account/mainnet/${address}/balance`,
          { headers }
        );
        if (solResp.ok) {
          const solData = await solResp.json() as { solana?: string; lamports?: string };
          const sol = parseFloat(solData.solana ?? "0");
          const lamports = solData.lamports ?? "0";
          if (import.meta.env.DEV) console.log("[sol] Moralis native SOL:", sol, "USD:", solUsdPrice ? (sol * solUsdPrice).toFixed(2) : "n/a");
          updatedBalances.push({
            mint: "native",
            symbol: "SOL",
            balance: sol,
            rawBalance: lamports,
            decimals: 9,
            usdValue: solUsdPrice != null ? sol * solUsdPrice : null,
          });
        } else {
          console.warn("[sol] Moralis SOL balance error:", solResp.status);
        }
      } catch (solErr) {
        console.warn("[sol] native SOL balance fetch failed:", solErr instanceof Error ? solErr.message : String(solErr));
      }

      // Fetch all SPL token balances via Moralis
      try {
        const tokResp = await fetch(
          `${MORALIS_SOLANA_BASE}/account/mainnet/${address}/tokens`,
          { headers }
        );
        if (tokResp.ok) {
          const tokData = await tokResp.json() as Array<{
            mint: string;
            symbol?: string;
            name?: string;
            amount: string;
            decimals: string;
          }>;
          for (const tok of tokData) {
            const decimals = parseInt(tok.decimals, 10) || 0;
            const rawAmount = tok.amount ?? "0";
            const balance = parseInt(rawAmount, 10) / Math.pow(10, decimals);
            updatedBalances.push({
              mint: tok.mint,
              symbol: tok.symbol || tok.name || tok.mint.slice(0, 6),
              balance,
              rawBalance: rawAmount,
              decimals,
              usdValue: null, // SPL token prices would need individual lookups
            });
          }
        } else {
          console.warn("[sol] Moralis SPL tokens error:", tokResp.status);
        }
      } catch (tokErr) {
        console.warn("[sol] SPL token balance fetch failed:", tokErr instanceof Error ? tokErr.message : String(tokErr));
      }

      if (import.meta.env.DEV) console.log("[sol] fetchBalances complete:", updatedBalances.length, "balances", updatedBalances.map(b => `${b.symbol}:${b.balance}`));
      this.state = {
        ...this.state,
        tokenBalances: updatedBalances,
        balanceLoading: false,
        balanceError: null,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[sol] fetchBalances error:", msg);
      this.state = {
        ...this.state,
        balanceLoading: false,
        balanceError: msg,
      };
    }
  }

  /** Convenience lookup: get balance for a specific mint address. */
  getBalanceForMint(mint: string): TokenBalance | null {
    return this.state.tokenBalances.find((b) => b.mint === mint) ?? null;
  }
}

export const solanaService = new SolanaService();
